import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { CEThunkPayload } from "../../api/gateway/carexGateway";
import { AxiosResponse } from "axios";

import { UserMetricsState, TrackEventRequest, TrackEventsBatchRequest, GetEventsRequest } from "./userMetrics.types";

import UserMetrics from "../../api/UserMetrics.api";

const initialState: UserMetricsState = {
    isTrackingEvent: false,
    isTrackingEventsBatch: false,
    isLoadingEvents: false,
    events: [],
    trackEventSuccess: false,
    trackEventsBatchSuccess: false,
    error: null,
};

const payloadFactory = (payload: CEThunkPayload<any>, service: Promise<any>) => {
    const success = payload.onSuccess ? payload.onSuccess : (res: AxiosResponse) => Promise.resolve(res);
    const error = payload.onError ? payload.onError : (err: AxiosResponse) => Promise.reject(err);
    return service.then(success).catch(error);
};

export const trackUserMetricEventThunkAction = createAsyncThunk(
    "trackUserMetricEvent",
    (payload: CEThunkPayload<TrackEventRequest>) => payloadFactory(payload, UserMetrics.trackEvent(payload))
);

export const trackUserMetricEventsBatchThunkAction = createAsyncThunk(
    "trackUserMetricEventsBatch",
    (payload: CEThunkPayload<TrackEventsBatchRequest>) => payloadFactory(payload, UserMetrics.trackEventsBatch(payload))
);

export const getUserMetricEventsThunkAction = createAsyncThunk(
    "getUserMetricEvents",
    (payload: CEThunkPayload<GetEventsRequest>) => payloadFactory(payload, UserMetrics.getEvents(payload.data))
);

const userMetricsSlice = createSlice({
    name: "userMetrics",
    initialState,
    reducers: {
        resetUserMetricsState: () => initialState,
        clearError: state => {
            state.error = null;
        },
    },
    extraReducers: builder => {
        // Track single event
        builder.addCase(trackUserMetricEventThunkAction.pending, state => {
            state.isTrackingEvent = true;
            state.trackEventSuccess = false;
            state.error = null;
        });
        builder.addCase(trackUserMetricEventThunkAction.fulfilled, state => {
            state.isTrackingEvent = false;
            state.trackEventSuccess = true;
        });
        builder.addCase(trackUserMetricEventThunkAction.rejected, (state, action) => {
            state.isTrackingEvent = false;
            state.trackEventSuccess = false;
            state.error = action.error.message || "Failed to track event";
        });

        // Track batch events
        builder.addCase(trackUserMetricEventsBatchThunkAction.pending, state => {
            state.isTrackingEventsBatch = true;
            state.trackEventsBatchSuccess = false;
            state.error = null;
        });
        builder.addCase(trackUserMetricEventsBatchThunkAction.fulfilled, state => {
            state.isTrackingEventsBatch = false;
            state.trackEventsBatchSuccess = true;
        });
        builder.addCase(trackUserMetricEventsBatchThunkAction.rejected, (state, action) => {
            state.isTrackingEventsBatch = false;
            state.trackEventsBatchSuccess = false;
            state.error = action.error.message || "Failed to track events batch";
        });

        // Get events
        builder.addCase(getUserMetricEventsThunkAction.pending, state => {
            state.isLoadingEvents = true;
            state.error = null;
        });
        builder.addCase(getUserMetricEventsThunkAction.fulfilled, (state, action) => {
            state.isLoadingEvents = false;
            if (action.payload) {
                state.events = action.payload.rows || action.payload || [];
            }
        });
        builder.addCase(getUserMetricEventsThunkAction.rejected, (state, action) => {
            state.isLoadingEvents = false;
            state.error = action.error.message || "Failed to load events";
        });
    },
});

export const { resetUserMetricsState, clearError } = userMetricsSlice.actions;
export default userMetricsSlice.reducer;
